import { Info } from 'lucide-react';
import React, { useState } from 'react';
import { FULL_EHR_FEATURE_PILL } from '../sandboxCopy';

interface FullEhrFeaturePillProps {
  className?: string;
}

/**
 * Small "FULL EHR FEATURE ⓘ" pill rendered near the top of every sandbox
 * pop-up that demonstrates real EHR functionality without being interactive
 * in the demo. The tooltip must open on tap as well as hover, which the
 * shared Radix Tooltip doesn't support, so the reveal is hand-rolled: hover
 * open/close only on hover-capable devices (touch browsers synthesize
 * mouseenter right before click, which would make a tap toggle open-then-shut),
 * click toggles everywhere, blur closes.
 */
const FullEhrFeaturePill: React.FC<FullEhrFeaturePillProps> = ({ className }) => {
  const [open, setOpen] = useState(false);

  const hoverCapable = () => window.matchMedia?.('(hover: hover)').matches ?? false;

  return (
    <span className={`relative inline-flex ${className ?? ''}`}>
      <button
        type="button"
        className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-bold tracking-wide text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 dark:bg-dark-gray-700 dark:text-gray-300 dark:ring-dark-gray-500"
        aria-label={`${FULL_EHR_FEATURE_PILL.label}: ${FULL_EHR_FEATURE_PILL.tooltip}`}
        onClick={() => setOpen(prev => !prev)}
        onMouseEnter={() => hoverCapable() && setOpen(true)}
        onMouseLeave={() => hoverCapable() && setOpen(false)}
        onBlur={() => setOpen(false)}
      >
        {FULL_EHR_FEATURE_PILL.label}
        <Info className="h-3.5 w-3.5" aria-hidden />
      </button>
      {open ? (
        <span
          role="tooltip"
          className="absolute left-0 top-full z-10 mt-1.5 w-64 rounded-lg bg-gray-900 px-3 py-2 text-xs font-medium leading-relaxed text-white shadow-lg dark:bg-dark-gray-600"
        >
          {FULL_EHR_FEATURE_PILL.tooltip}
        </span>
      ) : null}
    </span>
  );
};

export default FullEhrFeaturePill;
